/**
 * Client-side structured logger.
 * Buffers error/warn entries and periodically flushes to server via POST /api/client-logs.
 * In dev mode, also writes to console for immediate visibility.
 */

interface LogEntry {
  level: 'error' | 'warn';
  tag: string;
  msg: string;
  data?: Record<string, unknown>;
  timestamp: string;
  url: string;
  userAgent: string;
}

const FLUSH_INTERVAL_MS = 10_000;
const MAX_BUFFER_SIZE = 50;
const MAX_BATCH_SIZE = 20;

const buffer: LogEntry[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;

function makeEntry(level: 'error' | 'warn', tag: string, msg: string, data?: Record<string, unknown>): LogEntry {
  return {
    level,
    tag,
    msg,
    data,
    timestamp: new Date().toISOString(),
    url: window.location.pathname,
    userAgent: navigator.userAgent,
  };
}

function enqueue(entry: LogEntry): void {
  if (buffer.length >= MAX_BUFFER_SIZE) {
    buffer.shift(); // drop oldest
  }
  buffer.push(entry);
}

async function flush(): Promise<void> {
  if (buffer.length === 0) return;

  const batch = buffer.splice(0, MAX_BATCH_SIZE);

  try {
    const res = await fetch('/api/client-logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entries: batch }),
      keepalive: true, // survive page unload
    });
    if (!res.ok && buffer.length < MAX_BUFFER_SIZE) {
      // Put back on failure (if there's room)
      buffer.unshift(...batch);
    }
  } catch {
    // Network error — put back
    if (buffer.length < MAX_BUFFER_SIZE) {
      buffer.unshift(...batch);
    }
  }
}

function ensureTimer(): void {
  if (flushTimer) return;
  flushTimer = setInterval(() => { flush(); }, FLUSH_INTERVAL_MS);
}

export const clientLog = {
  error(tag: string, msg: string, data?: Record<string, unknown>): void {
    if (import.meta.env.DEV) console.error(`[${tag}]`, msg, data);
    const entry = makeEntry('error', tag, msg, data);
    enqueue(entry);
    ensureTimer();
    // Flush errors immediately
    flush();
  },

  warn(tag: string, msg: string, data?: Record<string, unknown>): void {
    if (import.meta.env.DEV) console.warn(`[${tag}]`, msg, data);
    const entry = makeEntry('warn', tag, msg, data);
    enqueue(entry);
    ensureTimer();
  },

  /** Force flush (call on beforeunload) */
  flush,
};

// Flush on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => { flush(); });
}
